import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Extractions from './pages/Extractions';
import ExtractionDetail from './pages/ExtractionDetail';
import Login from './pages/Login';
import Mailboxes    from './pages/Mailboxes';
import Policies     from './pages/Policies';
import ApiKeys      from './pages/ApiKeys';
import FeatureFlags from './pages/FeatureFlags';

// ─── Protected route wrapper ──────────────────────────────────────────────────
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 32, height: 32,
            border: '2px solid var(--border)', borderTopColor: 'var(--accent)',
            borderRadius: '50%', animation: 'spin 0.7s linear infinite',
            margin: '0 auto 12px',
          }} />
          <p style={{ color: 'var(--text-3)', fontSize: '13px' }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
};

// ─── Authenticated layout (sidebar + content) ─────────────────────────────────
const AppLayout: React.FC = () => {
  const { logout } = useAuth();
  const navigate   = useNavigate();

  // Handle 401 from the API client (token expired / revoked)
  useEffect(() => {
    const handler = () => {
      logout();
      navigate('/login', { replace: true });
    };
    window.addEventListener('falcon:unauthorized', handler);
    return () => window.removeEventListener('falcon:unauthorized', handler);
  }, [logout, navigate]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
        <Routes>
          <Route path="/"                element={<Dashboard />} />
          <Route path="/extractions"     element={<Extractions />} />
          <Route path="/extractions/:id" element={<ExtractionDetail />} />
          <Route path="/mailboxes"       element={<Mailboxes />} />
          <Route path="/policies"        element={<Policies />} />
          <Route path="/api-keys"        element={<ApiKeys />} />
          <Route path="/feature-flags"  element={<FeatureFlags />} />
          <Route path="*"               element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// ─── Root app ─────────────────────────────────────────────────────────────────
const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        } />
      </Routes>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
